import AdminApi from '../../services/api';
import swal from 'sweetalert2';
import session from "../../services/session";
import CONFIG_COMPANY from "../../services/myConfig";
import Loc from "../../services/localization";

const AdminApiService = new AdminApi();
const LocService = new Loc();

export default {
  data() {
    return {
      locale: LocService,
      role: '',
      loading: false,
      error: '',
      suppressWatch: true,
      filters: {
        fromDate: null,
        toDate: null,
      },
      report: {
        registrationsByMonth: [],
        deletedUsersByMonth: [],
        expiringThisMonth: 0,
        renewalByMonth: [],
        cohortRetentionByRegistrationMonth: [],
      },
    };
  },
  computed: {
    monthsRange() {
      if (!this.filters.fromDate || !this.filters.toDate) return [];
      const from = this.$moment(this.filters.fromDate, 'YYYY-MM-DD', true);
      const to = this.$moment(this.filters.toDate, 'YYYY-MM-DD', true);
      if (!from.isValid() || !to.isValid() || from.isAfter(to)) return [];
      const cursor = from.clone().startOf('month');
      const end = to.clone().startOf('month');
      const result = [];
      while (cursor.isSameOrBefore(end, 'month')) {
        result.push(cursor.format('YYYY-MM'));
        cursor.add(1, 'month');
      }
      return result;
    },
    registrationsSeries() {
      const map = new Map((this.report.registrationsByMonth || []).map(i => [i.month, Number(i.count || 0)]));
      return this.monthsRange.map(m => ({ month: m, count: map.get(m) || 0 }));
    },
    deletedSeries() {
      const map = new Map((this.report.deletedUsersByMonth || []).map(i => [i.month, Number(i.count || 0)]));
      return this.monthsRange.map(m => ({ month: m, count: map.get(m) || 0 }));
    },
    renewalSeries() {
      const map = new Map((this.report.renewalByMonth || []).map(i => [i.month, {
        expiredUsers: Number(i.expiredUsers || 0),
        notRenewedUsers: Number(i.notRenewedUsers || 0),
        renewalRatePercent: Number(i.renewalRatePercent || 0),
      }]));
      return this.monthsRange.map(m => {
        const row = map.get(m) || { expiredUsers: 0, notRenewedUsers: 0, renewalRatePercent: 0 };
        return Object.assign({ month: m }, row);
      });
    },
    cohortSeries() {
      const map = new Map((this.report.cohortRetentionByRegistrationMonth || []).map(i => [i.cohortMonth, {
        cohortSize: Number(i.cohortSize || 0),
        m1Active: Number(i.m1Active || 0),
        m2Active: Number(i.m2Active || 0),
        m3Active: Number(i.m3Active || 0),
      }]));
      return this.monthsRange.map(m => {
        const row = map.get(m) || { cohortSize: 0, m1Active: 0, m2Active: 0, m3Active: 0 };
        return Object.assign({ cohortMonth: m }, row);
      });
    },
    totalRegistrations() {
      return this.registrationsSeries.reduce((sum, i) => sum + i.count, 0);
    },
    totalDeletedUsers() {
      return this.deletedSeries.reduce((sum, i) => sum + i.count, 0);
    },
    avgRenewalRate() {
      const rows = this.renewalSeries.filter(i => Number(i.expiredUsers || 0) > 0);
      if (!rows.length) return 0;
      return rows.reduce((sum, i) => sum + Number(i.renewalRatePercent || 0), 0) / rows.length;
    },
    maxRegistrationDeleted() {
      let max = 0;
      this.registrationsSeries.forEach(i => { if (i.count > max) max = i.count; });
      this.deletedSeries.forEach(i => { if (i.count > max) max = i.count; });
      return max;
    },
    maxRenewalUsers() {
      let max = 0;
      this.renewalSeries.forEach(i => {
        if (i.expiredUsers > max) max = i.expiredUsers;
        if (i.notRenewedUsers > max) max = i.notRenewedUsers;
      });
      return max;
    },
    hasData() {
      if (this.report.expiringThisMonth > 0) return true;
      if (this.totalRegistrations > 0 || this.totalDeletedUsers > 0) return true;
      if (this.renewalSeries.some(i => i.expiredUsers > 0 || i.notRenewedUsers > 0)) return true;
      return this.cohortSeries.some(i => i.cohortSize > 0 || i.m1Active > 0 || i.m2Active > 0 || i.m3Active > 0);
    }
  },
  methods: {
    loadReport: loadReport,
    isDateRangeValid: isDateRangeValid,
    formatNumber: formatNumber,
    formatPercent: formatPercent,
    monthLabel: monthLabel,
    chartWidth: chartWidth,
    rowPercent: rowPercent,
    searchReport: searchReport,
  },
  beforeMount() {
    if (session.getRole() !== 'ROLE_ADMIN' && session.getRole() !== 'ROLE_SUPER_ADMIN') {
      this.$router.push(CONFIG_COMPANY.HOME);
      return;
    }
    this.role = session.getRole();
    const end = this.$moment(new Date());
    const start = end.clone().subtract(1, 'year');
    this.filters.fromDate = start.format('YYYY-MM-DD');
    this.filters.toDate = end.format('YYYY-MM-DD');
    this.suppressWatch = false;
    this.loadReport();
  },
  watch: {
    'filters.fromDate': function () {
      if (this.suppressWatch) return;
      this.loadReport();
    },
    'filters.toDate': function () {
      if (this.suppressWatch) return;
      this.loadReport();
    }
  }
};

function isDateRangeValid() {
  if (!this.filters.fromDate || !this.filters.toDate) return false;
  const from = this.$moment(this.filters.fromDate, 'YYYY-MM-DD', true);
  const to = this.$moment(this.filters.toDate, 'YYYY-MM-DD', true);
  return from.isValid() && to.isValid() && !from.isAfter(to);
}

function loadReport() {
  if (!this.isDateRangeValid()) {
    this.error = this.locale.t('label_invalid_date_range');
    return;
  }
  this.error = '';
  this.loading = true;

  const model = {
    fromDate: this.$moment(this.filters.fromDate).format('DD-MM-YYYY'),
    toDate: this.$moment(this.filters.toDate).format('DD-MM-YYYY'),
  };

  AdminApiService.getUsersSummaryReport(model)
    .then((response) => {
      const data = response.data || {};
      this.report = {
        registrationsByMonth: data.registrationsByMonth || [],
        deletedUsersByMonth: data.deletedUsersByMonth || [],
        expiringThisMonth: Number(data.expiringThisMonth || 0),
        renewalByMonth: data.renewalByMonth || [],
        cohortRetentionByRegistrationMonth: data.cohortRetentionByRegistrationMonth || [],
      };
      this.loading = false;
    })
    .catch((error) => {
      this.loading = false;
      this.error = this.locale.t('label_no_data_for_period');
      swal(LocService.t("label_error"), error.data.localized_error_message, 'error');
    });
}

function formatNumber(value) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function monthLabel(yyyyMM) {
  if (!yyyyMM || yyyyMM.indexOf('-') < 0) return yyyyMM;
  const parts = yyyyMM.split('-');
  const year = parts[0];
  const month = parts[1];
  let monthKey = 'month_short_january';
  switch (month) {
    case '01': monthKey = 'month_short_january'; break;
    case '02': monthKey = 'month_short_february'; break;
    case '03': monthKey = 'month_short_march'; break;
    case '04': monthKey = 'month_short_april'; break;
    case '05': monthKey = 'month_short_may'; break;
    case '06': monthKey = 'month_short_june'; break;
    case '07': monthKey = 'month_short_july'; break;
    case '08': monthKey = 'month_short_august'; break;
    case '09': monthKey = 'month_short_september'; break;
    case '10': monthKey = 'month_short_october'; break;
    case '11': monthKey = 'month_short_november'; break;
    case '12': monthKey = 'month_short_december'; break;
    default: break;
  }
  return `${this.locale.t(monthKey)} ${year}`;
}

function chartWidth(value, max) {
  const v = Number(value) || 0;
  const m = Number(max) || 0;
  if (m <= 0) return '0%';
  const ratio = (v / m) * 100;
  return `${Math.max(6, Math.min(100, ratio)).toFixed(2)}%`;
}

function rowPercent(active, size) {
  const s = Number(size || 0);
  if (s <= 0) return '0.0%';
  const a = Number(active || 0);
  return `${((a / s) * 100).toFixed(1)}%`;
}

function searchReport() {
  this.loadReport();
}



// WEBPACK FOOTER //
// ./src/components/Report/UserReport.js